import { useEffect, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  useController,
  UseFormGetFieldState,
  useWatch,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { QueueRuleAction, Schema } from '@rossum/api-client/schemas';
import { Tile, TileQuickAction } from '@rossum/components/tile';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { ActionHeader } from '../actions/createHeader';
import { canChangeEvent, getDefaultEvent } from '../actions/events';
import { ActionAutocomplete } from './ActionAutocomplete';
import { useActionOptions } from './useActionOptions';

type ActionFormProps = {
  action: QueueRuleAction;
  index: number;
  remove: (index: number) => void;
  control: Control<{ actions: QueueRuleAction[] }, unknown>;
  schema: Schema;
  getFieldState: UseFormGetFieldState<FieldValues>;
  disabled?: boolean;
};

export const ActionForm = ({
  action,
  remove,
  index,
  control,
  schema,
  getFieldState,
  disabled = false,
}: ActionFormProps) => {
  const intl = useIntl();

  // Newly created actions start as expanded
  const [expanded, setExpanded] = useState(action.type === '');

  const ruleActions = useWatch({
    control,
    name: 'actions',
  });

  const { actionOptions, selectableActions } = useActionOptions(ruleActions);

  const fieldState = getFieldState(`actions.${index}`);

  // Auto-expand on error
  useEffect(() => {
    if (fieldState.error) {
      setExpanded(true);
    }
  }, [fieldState.error]);

  const { field: enabledField } = useController({
    control,
    name: `actions.${index}.enabled`,
  });

  const alreadyUsedSchemaIdsPerType = useMemo(
    () =>
      ruleActions.reduce<Record<string, string[]>>((acc, a) => {
        const schemaId = getUnsafe(a.payload, 'schema_id');
        if (a.type && typeof schemaId === 'string') {
          return {
            ...acc,
            [a.type]: [...(acc[a.type] ?? []), schemaId],
          };
        }
        return acc;
      }, {}),
    [ruleActions]
  );

  return (
    <Controller
      control={control}
      name={`actions.${index}`}
      render={({ field }) => {
        const { value } = field;
        const enabled = enabledField.value;

        const actionDef = actionOptions.find(a => a.resolveAction(value));

        const Form = actionDef?.form;
        const Header = actionDef?.header;
        const Icon = actionDef?.icon;

        const formProps = {
          value: value.payload,
          onChange: (payload: unknown) => {
            field.onChange({
              ...value,
              type: value.type,
              payload,
            });
          },
          schema,
          errors: getFieldState(`actions.${index}.payload`).error,
          alreadyUsedSchemaIds: value.type
            ? alreadyUsedSchemaIdsPerType[value.type]
            : undefined,
          event: actionDef
            ? value.event || getDefaultEvent(actionDef)
            : undefined,
        } as const;

        const fullWidth =
          actionDef && 'fullWidth' in actionDef ? actionDef.fullWidth : false;

        const title = actionDef
          ? actionDef.name
          : value.type
            ? intl.formatMessage({
                id: 'features.queueSettings.rules.detail.then.unknownAction',
              })
            : intl.formatMessage({
                id: 'features.queueSettings.rules.detail.then.newAction',
              });

        const eventLabel =
          actionDef && canChangeEvent(actionDef)
            ? intl.formatMessage({
                id: `features.queueSettings.rules.events.${value.event || getDefaultEvent(actionDef)}`,
              })
            : undefined;

        const description = actionDef ? (
          (Header && value.payload) || eventLabel ? (
            <Stack color={enabled ? undefined : 'text.disabled'}>
              {eventLabel ? (
                <Typography variant="caption">{eventLabel}</Typography>
              ) : null}
              {Header && value.payload ? (
                <Header
                  value={value.payload}
                  schema={schema}
                  RenderComponent={ActionHeader}
                />
              ) : null}
            </Stack>
          ) : undefined
        ) : value.type ? (
          intl.formatMessage({
            id: 'features.queueSettings.rules.detail.then.unknownActionDescription',
          })
        ) : (
          intl.formatMessage({
            id: 'features.queueSettings.rules.detail.then.newActionDescription',
          })
        );

        const quickActions: TileQuickAction[] = [
          {
            key: 'delete',
            iconProps: {
              Icon: IconTrash,
              state: false,
              disabled,
            },
            disabled,
            onClick: () => remove(index),
            'data-cy': 'action-tile-delete',
          },
        ];

        const expandableContent = (
          <Stack direction="column" spacing={2} divider={<Divider />}>
            <Stack direction="row" spacing={3} p={3}>
              <Stack width="50%">
                {actionDef ? (
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="span"
                    >
                      {actionDef.description}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
              <Stack spacing={2} width="50%">
                <ActionAutocomplete
                  options={actionOptions}
                  selectableActions={selectableActions}
                  value={value}
                  error={getUnsafe(
                    fieldState.error,
                    'type.message'
                  )?.toString()}
                  onChange={s => {
                    // Keep the action payload when changing just a message type
                    if (
                      s?.type === 'show_message' &&
                      value.type === 'show_message' &&
                      typeof value.payload === 'object' &&
                      s.payload &&
                      typeof s.payload === 'object' &&
                      'type' in s.payload
                    ) {
                      field.onChange({
                        ...value,
                        ...s,
                        payload: {
                          ...value.payload,
                          type: s.payload.type,
                        },
                      });
                    } else {
                      field.onChange({ ...value, ...s });
                    }
                  }}
                />
                {actionDef && canChangeEvent(actionDef) ? (
                  <Autocomplete
                    size="small"
                    options={actionDef.supportedEvents}
                    getOptionLabel={option =>
                      intl.formatMessage({
                        id: `features.queueSettings.rules.events.${option}`,
                      })
                    }
                    disableClearable
                    value={value.event || getDefaultEvent(actionDef)}
                    onChange={(_e, vs) =>
                      field.onChange({
                        ...value,
                        event: vs,
                      })
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={intl.formatMessage({
                          id: 'features.queueSettings.rules.events.autocomplete.label',
                        })}
                        InputLabelProps={{ shrink: true }}
                        disabled={disabled}
                      />
                    )}
                  />
                ) : null}
                {Form && !fullWidth ? <Form {...formProps} /> : null}
              </Stack>
            </Stack>
            {Form && fullWidth ? (
              <Stack spacing={2} p={3}>
                <Form {...formProps} />
              </Stack>
            ) : null}
          </Stack>
        );

        return (
          <Tile
            iconProps={
              Icon
                ? {
                    content: Icon,
                    error: fieldState.error !== undefined,
                  }
                : undefined
            }
            title={title}
            description={description}
            disabled={disabled}
            toggleAction={{
              checked: enabled,
              disabled,
              onChange: () => enabledField.onChange(!enabled),
            }}
            quickActions={quickActions}
            expandable={{
              expanded,
              onToggleExpanded: () => setExpanded(prev => !prev),
              content: expandableContent,
            }}
            dragHandle={null}
            data-cy="action-tile"
          />
        );
      }}
    />
  );
};
